import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { CoachPicker } from "./CoachPicker";
import { ThemePicker } from "./ThemePicker";
import { ProfilePicUploader } from "./ProfilePicUploader";
import { StatsEditor } from "./StatsEditor";
import { NotificationPrompt } from "./NotificationPrompt";
import { ReferralTab } from "./tabs/ReferralTab";
import { HealthSettingsSection } from "./HealthSettingsSection";
import { AppTheme, CoachingStyle } from "@/types/evolve";
import {
  Bell,
  Palette,
  RotateCcw,
  UserCircle,
  HelpCircle,
  RefreshCw,
  CreditCard,
  Shield,
  Fingerprint,
  Loader2,
  Lock,
  Mail,
  Settings,
  ExternalLink,
} from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useBiometricAuth } from "@/hooks/useBiometricAuth";
import { openNativeSettings, isDespiaNative } from "@/utils/despia";

interface UserStats {
  age?: number;
  sex?: string;
  height?: string;
  weight?: number;
  goal?: string;
  specificGoal?: string;
  experience?: string;
  daysPerWeek?: number;
  equipment?: string;
  injuries?: string;
  dietPreferences?: string;
}

interface NotificationPreferences {
  trainingReminders?: boolean;
  weighInNudges?: boolean;
  motivationalMessages?: boolean;
  progressUpdates?: boolean;
  pushEnabled?: boolean;
}

interface NutritionPreferences {
  showMicronutrients: boolean;
  activityLevel: string;
}

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId?: string;
  userName?: string;
  avatarUrl?: string;
  onAvatarChange?: (url: string) => void;
  coachingStyle: CoachingStyle;
  onCoachingStyleChange: (style: CoachingStyle) => void;
  theme: AppTheme;
  onThemeChange: (updates: Partial<AppTheme>) => void;
  onThemeReset: () => void;
  notificationsEnabled: boolean;
  onEnableNotifications: () => void;
  notificationPreferences?: NotificationPreferences;
  onNotificationPreferencesChange?: (prefs: NotificationPreferences) => void;
  userStats?: UserStats;
  onStatsChange?: (stats: UserStats) => Promise<void>;
  onReplayTour?: () => void;
  referralCode?: string | null;
  isEvolverFounder?: boolean;
  nutritionPreferences?: NutritionPreferences;
  onNutritionPreferencesChange?: (prefs: NutritionPreferences) => void;
  displayName?: string;
  leaderboardPrivacy?: "full_name" | "nickname" | "anonymous" | "hidden";
  onPrivacyChange?: (displayName: string, privacy: "full_name" | "nickname" | "anonymous" | "hidden") => void;
  onRefreshSubscription?: () => void;
  subscriptionLoading?: boolean;
  onLogout?: () => void;
}

export function SettingsModal({
  isOpen,
  onClose,
  userId,
  userName,
  avatarUrl,
  onAvatarChange,
  coachingStyle,
  onCoachingStyleChange,
  theme,
  onThemeChange,
  onThemeReset,
  notificationsEnabled,
  onEnableNotifications,
  notificationPreferences = {
    trainingReminders: true,
    weighInNudges: true,
    motivationalMessages: true,
    progressUpdates: true,
    pushEnabled: false,
  },
  onNotificationPreferencesChange,
  userStats,
  onStatsChange,
  onReplayTour,
  referralCode,
  isEvolverFounder,
  nutritionPreferences = { showMicronutrients: false, activityLevel: "moderate" },
  onNutritionPreferencesChange,
  displayName = "",
  leaderboardPrivacy = "full_name",
  onPrivacyChange,
  onRefreshSubscription,
  subscriptionLoading = false,
  onLogout,
}: SettingsModalProps) {
  const [localPrefs, setLocalPrefs] = useState(notificationPreferences);
  const [localDisplayName, setLocalDisplayName] = useState(displayName);
  const [localPrivacy, setLocalPrivacy] = useState(leaderboardPrivacy);
  const [localNutritionPrefs, setLocalNutritionPrefs] = useState(nutritionPreferences);
  const [showBiometricSetup, setShowBiometricSetup] = useState(false);
  const [biometricPassword, setBiometricPassword] = useState("");
  const [biometricEmail, setBiometricEmail] = useState("");
  const [isSendingTestPush, setIsSendingTestPush] = useState(false);

  // Biometric auth hook
  const {
    isNativeApp,
    isBiometricAvailable,
    isBiometricEnabled,
    isAuthenticating,
    enableBiometricAuth,
    disableBiometricAuth,
  } = useBiometricAuth(userId);

  const handleBiometricToggle = async (enabled: boolean) => {
    if (enabled) {
      // Show password dialog to capture credentials for secure storage
      setShowBiometricSetup(true);
    } else {
      await disableBiometricAuth();
    }
  };

  const handleBiometricSetupConfirm = async () => {
    if (!biometricEmail || !biometricPassword) {
      toast.error("Please enter your email and password");
      return;
    }

    const success = await enableBiometricAuth(biometricEmail, biometricPassword);
    if (success) {
      setShowBiometricSetup(false);
      setBiometricPassword("");
      setBiometricEmail("");
    }
  };

  const handlePrefChange = (key: keyof NotificationPreferences, value: boolean) => {
    const newPrefs = { ...localPrefs, [key]: value };
    setLocalPrefs(newPrefs);
    onNotificationPreferencesChange?.(newPrefs);
  };

  const handleNutritionPrefChange = (key: keyof NutritionPreferences, value: boolean | string) => {
    const newPrefs = { ...localNutritionPrefs, [key]: value };
    setLocalNutritionPrefs(newPrefs);
    onNutritionPreferencesChange?.(newPrefs);
  };

  const handleSendTestPush = async () => {
    setIsSendingTestPush(true);
    try {
      const { data, error } = await supabase.functions.invoke("send-test-push");

      console.log("Test push response:", data, error);

      if (error) {
        toast.error(`Push failed: ${error.message}`);
        return;
      }

      if (data?.success) {
        toast.success(data.message || "Test push sent!", {
          description: `External ID: ${data.externalId}`,
        });
      } else {
        toast.error(data?.error || "Failed to send push", {
          description: data?.details ? JSON.stringify(data.details) : undefined,
        });
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : "Unknown error";
      toast.error(`Request failed: ${errorMessage}`);
    } finally {
      setIsSendingTestPush(false);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      modal={true}
    >
      <DialogContent id="settings-modal" className="max-w-md max-h-[85vh] overflow-y-auto bg-card pb-32">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Settings</DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="customize" className="mt-4">
          <TabsList className="w-full grid grid-cols-3">
            <TabsTrigger value="profile" className="flex items-center gap-1 px-1.5">
              <UserCircle className="w-4 h-4" />
              <span className="text-xs hidden sm:inline">Profile</span>
            </TabsTrigger>
            <TabsTrigger value="customize" id="customize-tab-trigger" className="flex items-center gap-1 px-1.5">
              <Palette className="w-4 h-4" />
              <span className="text-xs hidden sm:inline">Theme</span>
            </TabsTrigger>
            <TabsTrigger value="notifications" className="flex items-center gap-1 px-1.5">
              <Bell className="w-4 h-4" />
              <span className="text-xs hidden sm:inline">Alerts</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="profile" className="mt-4 space-y-6">
            {/* Profile Picture */}
            <div className="space-y-4">
              <div className="text-center">
                <h3 className="font-medium mb-1">Profile Picture</h3>
                <p className="text-xs text-muted-foreground">Add a photo to personalize your profile</p>
              </div>
              {userId && onAvatarChange && (
                <ProfilePicUploader
                  userId={userId}
                  currentAvatarUrl={avatarUrl}
                  onUpload={onAvatarChange}
                  userName={userName}
                />
              )}
            </div>

            {/* Leaderboard Privacy */}
            <div className="space-y-4 pt-4 border-t border-border/50">
              <div>
                <h3 className="font-medium mb-1">Leaderboard Privacy</h3>
                <p className="text-xs text-muted-foreground">Choose how your name appears on the leaderboard</p>
              </div>

              <div className="space-y-3">
                <Select
                  value={localPrivacy}
                  onValueChange={(v: "full_name" | "nickname" | "anonymous" | "hidden") => {
                    setLocalPrivacy(v);
                    onPrivacyChange?.(localDisplayName, v);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="full_name">Show Full Name</SelectItem>
                    <SelectItem value="nickname">Show Nickname</SelectItem>
                    <SelectItem value="anonymous">Anonymous Evolver</SelectItem>
                    <SelectItem value="hidden">Hide from Leaderboard</SelectItem>
                  </SelectContent>
                </Select>

                {localPrivacy === "nickname" && (
                  <div className="space-y-2">
                    <Label className="text-sm">Your Nickname</Label>
                    <input
                      type="text"
                      value={localDisplayName}
                      onChange={(e) => {
                        setLocalDisplayName(e.target.value);
                        onPrivacyChange?.(e.target.value, localPrivacy);
                      }}
                      className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                      placeholder="Enter a nickname..."
                      maxLength={20}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Stats Section */}
            <div className="space-y-4 pt-4 border-t border-border/50">
              <div>
                <h3 className="font-medium mb-1">Your Stats</h3>
                <p className="text-xs text-muted-foreground">Update your fitness stats anytime</p>
              </div>
              {userStats && onStatsChange ? (
                <StatsEditor stats={userStats} onSave={onStatsChange} userId={userId} />
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">Complete onboarding to set your stats.</p>
              )}
            </div>

            {/* Nutrition Settings */}
            <div className="space-y-4 pt-4 border-t border-border/50">
              <div>
                <h3 className="font-medium mb-1">Nutrition Settings</h3>
                <p className="text-xs text-muted-foreground">Customize how the coach calculates your nutrition</p>
              </div>

              {/* Diet Preferences */}
              <div className="space-y-2">
                <Label className="text-sm">Diet Preferences</Label>
                <input
                  type="text"
                  value={userStats?.dietPreferences || ""}
                  onChange={(e) => {
                    if (onStatsChange && userStats) {
                      onStatsChange({ ...userStats, dietPreferences: e.target.value });
                    }
                  }}
                  className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  placeholder="e.g., Keto, Vegetarian, No restrictions"
                />
              </div>

              {/* Activity Level */}
              <div className="space-y-2">
                <Label className="text-sm">Activity Level</Label>
                <Select
                  value={localNutritionPrefs.activityLevel}
                  onValueChange={(v) => handleNutritionPrefChange("activityLevel", v)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sedentary">Sedentary (desk job)</SelectItem>
                    <SelectItem value="light">Light (1-2 workouts/week)</SelectItem>
                    <SelectItem value="moderate">Moderate (3-4 workouts/week)</SelectItem>
                    <SelectItem value="active">Active (5-6 workouts/week)</SelectItem>
                    <SelectItem value="very_active">Very Active (6-7+)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Micronutrients Toggle */}
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="micros" className="text-sm">
                    Show Micronutrients
                  </Label>
                  <p className="text-xs text-muted-foreground">Include vitamins & minerals in plans</p>
                </div>
                <Switch
                  id="micros"
                  checked={localNutritionPrefs.showMicronutrients}
                  onCheckedChange={(v) => handleNutritionPrefChange("showMicronutrients", v)}
                />
              </div>
            </div>

            {/* Health Integration */}
            <div className="pt-4 border-t border-border/50">
              <HealthSettingsSection userId={userId} />
            </div>

            {/* Security Settings - Only show on native app */}
            {isNativeApp && isBiometricAvailable && (
              <div className="space-y-4 pt-4 border-t border-border/50">
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-primary" />
                  <h3 className="font-medium">Security</h3>
                </div>
                <p className="text-xs text-muted-foreground">Secure your account with biometric authentication</p>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <Fingerprint className="w-5 h-5 text-primary" />
                    </div>
                    <div className="space-y-0.5">
                      <Label htmlFor="biometric" className="text-sm">
                        Face ID / Touch ID
                      </Label>
                      <p className="text-xs text-muted-foreground">Quick & secure sign in</p>
                    </div>
                  </div>
                  {isAuthenticating ? (
                    <Loader2 className="w-5 h-5 animate-spin text-primary" />
                  ) : (
                    <Switch id="biometric" checked={isBiometricEnabled} onCheckedChange={handleBiometricToggle} />
                  )}
                </div>
              </div>
            )}

            {/* Subscription Refresh */}
            {onRefreshSubscription && (
              <div className="pt-4 border-t border-border/50">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onRefreshSubscription}
                  disabled={subscriptionLoading}
                  className="w-full"
                >
                  {subscriptionLoading ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <CreditCard className="w-4 h-4 mr-2" />
                  )}
                  {subscriptionLoading ? "Checking..." : "Refresh Subscription Status"}
                </Button>
                <p className="text-xs text-muted-foreground text-center mt-2">
                  Use this if you purchased but premium isn't showing
                </p>
              </div>
            )}

            {/* Legal Links */}
            <div className="pt-4 border-t border-border/50">
              <div className="space-y-2">
                <h3 className="font-medium">Legal</h3>
                <div className="flex flex-col gap-2">
                  <a
                    href="https://www.apple.com/legal/internet-services/itunes/dev/stdeula/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-primary hover:underline flex items-center gap-1"
                  >
                    Terms of Use (EULA)
                    <ExternalLink className="w-3 h-3" />
                  </a>
                  <a
                    href="https://evolveaicoach.app/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-primary hover:underline flex items-center gap-1"
                  >
                    Privacy Policy
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </div>
            </div>

          </TabsContent>

          <TabsContent value="customize" className="mt-4 space-y-6">
            {/* Coach Section */}
            <div className="space-y-4">
              <div>
                <h3 className="font-medium mb-1">Coach Personality</h3>
                <p className="text-xs text-muted-foreground">Choose how your coach talks to you</p>
              </div>
              <CoachPicker selected={coachingStyle} onChange={onCoachingStyleChange} compact />
            </div>

            {/* Theme Section */}
            <div className="space-y-4 pt-4 border-t border-border/50">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium">App Theme</h3>
                  <p className="text-xs text-muted-foreground">Customize your experience</p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onThemeReset}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <RotateCcw className="w-4 h-4 mr-1" />
                  Reset
                </Button>
              </div>

              <ThemePicker theme={theme} onChange={onThemeChange} compact />
            </div>
          </TabsContent>

          <TabsContent value="notifications" className="mt-4">
            <div className="space-y-4">
              {notificationsEnabled ? (
                <div className="p-4 rounded-lg border border-primary/30 bg-primary/5">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full evolve-gradient flex items-center justify-center">
                      <Bell className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="font-medium text-sm">Notifications Enabled</p>
                      <p className="text-xs text-muted-foreground">You're 4.7× more likely to hit Immortal. 🔥</p>
                    </div>
                  </div>
                </div>
              ) : (
                <NotificationPrompt onEnable={onEnableNotifications} onDismiss={() => {}} isUpsell />
              )}

              {/* Notification Preferences */}
              <div className="space-y-4 pt-4 border-t border-border/50">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-medium">Notification Preferences</h4>
                    <p className="text-xs text-muted-foreground">Choose which notifications you want to receive</p>
                  </div>
                  {/* Info button for native - shows instructions toast */}
                  {isDespiaNative() && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        toast.info("Go to iOS Settings → Evolve → Notifications to manage permissions", {
                          duration: 5000,
                        });
                      }}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      <Settings className="w-4 h-4 mr-1" />
                      <ExternalLink className="w-3 h-3" />
                    </Button>
                  )}
                </div>

                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label htmlFor="training" className="text-sm">
                        Training Reminders
                      </Label>
                      <p className="text-xs text-muted-foreground">At your scheduled gym time</p>
                    </div>
                    <Switch
                      id="training"
                      checked={localPrefs.trainingReminders ?? true}
                      onCheckedChange={(v) => handlePrefChange("trainingReminders", v)}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label htmlFor="weighin" className="text-sm">
                        Weigh-in Nudges
                      </Label>
                      <p className="text-xs text-muted-foreground">Morning weigh-in reminders</p>
                    </div>
                    <Switch
                      id="weighin"
                      checked={localPrefs.weighInNudges ?? true}
                      onCheckedChange={(v) => handlePrefChange("weighInNudges", v)}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label htmlFor="motivation" className="text-sm">
                        Motivational Messages
                      </Label>
                      <p className="text-xs text-muted-foreground">Random daily hype messages</p>
                    </div>
                    <Switch
                      id="motivation"
                      checked={localPrefs.motivationalMessages ?? true}
                      onCheckedChange={(v) => handlePrefChange("motivationalMessages", v)}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label htmlFor="progress" className="text-sm">
                        Progress Updates
                      </Label>
                      <p className="text-xs text-muted-foreground">Weekly progress summaries</p>
                    </div>
                    <Switch
                      id="progress"
                      checked={localPrefs.progressUpdates ?? true}
                      onCheckedChange={(v) => handlePrefChange("progressUpdates", v)}
                    />
                  </div>
                </div>
              </div>

              {/* Debug Tools Section */}
              <div className="space-y-3 pt-4 border-t border-border/50">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-medium">Push Diagnostics</h4>
                    <p className="text-xs text-muted-foreground">Verify your notification setup</p>
                  </div>
                </div>

                {/* Status Card */}
                <div className="p-3 rounded-lg bg-card border border-border space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">Permission</span>
                    <span
                      className={`text-xs font-medium ${
                        typeof Notification !== "undefined" && Notification.permission === "granted"
                          ? "text-green-500"
                          : typeof Notification !== "undefined" && Notification.permission === "denied"
                            ? "text-red-500"
                            : "text-yellow-500"
                      }`}
                    >
                      {typeof Notification !== "undefined" ? Notification.permission : "unknown"}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">Linked Account</span>
                    <span className="text-xs font-medium text-foreground">
                      {userId ? "✓ Connected" : "Not logged in"}
                    </span>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col gap-2">
                  {typeof Notification !== "undefined" && Notification.permission === "default" && (
                    <Button variant="outline" size="sm" onClick={onEnableNotifications} className="w-full">
                      <Bell className="w-4 h-4 mr-2" />
                      Enable Notifications
                    </Button>
                  )}

                  {typeof Notification !== "undefined" && Notification.permission === "denied" && (
                    <div className="space-y-2">
                      <p className="text-xs text-muted-foreground">
                        Notifications were blocked. You'll need to <strong>reset</strong> the permission, not just
                        enable it.
                      </p>
                      {isDespiaNative() ? (
                        <div className="text-xs text-muted-foreground p-2 bg-secondary/50 rounded space-y-1">
                          <p className="font-medium text-foreground">To enable notifications:</p>
                          <p>
                            1. Open your iPhone's <strong>Settings</strong> app
                          </p>
                          <p>
                            2. Scroll down and tap <strong>Evolve</strong>
                          </p>
                          <p>
                            3. Tap <strong>Notifications</strong>
                          </p>
                          <p>
                            4. Toggle <strong>Allow Notifications</strong> on
                          </p>
                        </div>
                      ) : (
                        <div className="text-xs text-muted-foreground p-2 bg-secondary/50 rounded mb-2 space-y-1">
                          <p>
                            1. Click the <strong>lock icon</strong> in your address bar
                          </p>
                          <p>
                            2. Find <strong>Notifications</strong> setting
                          </p>
                          <p>
                            3. Change to <strong>"Ask"</strong> or <strong>"Allow"</strong>
                          </p>
                        </div>
                      )}
                      <Button
                        variant="default"
                        size="sm"
                        onClick={() => {
                          localStorage.setItem("pending_notification_prompt", "true");
                          window.location.reload();
                        }}
                        className="w-full"
                      >
                        <RefreshCw className="w-4 h-4 mr-2" />
                        I've Reset It — Refresh Now
                      </Button>
                    </div>
                  )}

                  <Button
                    variant={
                      typeof Notification !== "undefined" && Notification.permission === "granted"
                        ? "default"
                        : "outline"
                    }
                    size="sm"
                    onClick={handleSendTestPush}
                    disabled={isSendingTestPush}
                    className="w-full"
                  >
                    {isSendingTestPush ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Bell className="w-4 h-4 mr-2" />
                        Send Test Notification
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>

      {/* Biometric Setup Dialog - Using AlertDialog to avoid nested Dialog issues */}
      <AlertDialog open={showBiometricSetup} onOpenChange={setShowBiometricSetup}>
        <AlertDialogContent className="max-w-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Fingerprint className="w-5 h-5 text-primary" />
              Enable Face ID / Touch ID
            </AlertDialogTitle>
            <AlertDialogDescription>
              Enter your email and password to securely store your credentials for biometric login.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="biometric-email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="biometric-email"
                  type="email"
                  placeholder="Your email"
                  value={biometricEmail}
                  onChange={(e) => setBiometricEmail(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="biometric-password">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="biometric-password"
                  type="password"
                  placeholder="Your password"
                  value={biometricPassword}
                  onChange={(e) => setBiometricPassword(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Your credentials are encrypted and stored securely in your device's keychain.
            </p>
          </div>

          <AlertDialogFooter className="gap-2 sm:gap-0">
            <AlertDialogCancel
              onClick={() => {
                setBiometricPassword("");
                setBiometricEmail("");
              }}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBiometricSetupConfirm}
              disabled={isAuthenticating || !biometricEmail || !biometricPassword}
              className="bg-primary hover:bg-primary/90"
            >
              {isAuthenticating ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Fingerprint className="w-4 h-4 mr-2" />
              )}
              Verify & Enable
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}
